import { Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, Outlet } from 'react-router-dom';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import { useSelector, useDispatch } from 'react-redux';
import { clearUser } from '../features/auth/authSlice';

interface NavbarProps {
  [key: string]: any;
}

const Navbar = (props: NavbarProps) => {
  const user = useSelector((state: any) => state.auth?.user);
  const userEmail = user?.email || "monica@kimaru.ai";
  const userName = user?.email === "monica@kimaru.ai" ? "Monica" : "Sam";

  const [isExpanded, setIsExpanded] = useState(false);
  const [isManual, setIsManual] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef<any>(null);
  const sidebarRef = useRef<any>(null);

  const navigate = useNavigate();
  const dispatch: any = useDispatch();

  const API_BASE = 'https://api.stag.kimaru.ai/api/v1';

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      dispatch(clearUser());
      showSuccessToast('Logged out successfully!', 'top-right');
      navigate('/login');
    } catch (error: any) {
      console.error('Logout failed:', error);
      showErrorToast('Logout failed', 'top-right');
    }
  };

  const handleResetData = async () => {
    try {
      const [movementResponse, priceResponse] = await Promise.all([
        fetch(`${API_BASE}/movement_recommendation/reset_recommendations`, {
          method: 'POST',
          headers: { 'accept': 'application/json', 'user-email': userEmail },
          credentials: 'include',
        }),
        fetch(`${API_BASE}/price_recommendation/reset_recommendations`, {
          method: 'POST',
          headers: { 'accept': 'application/json', 'user-email': userEmail },
          credentials: 'include',
        })
      ]);

      if (!movementResponse.ok || !priceResponse.ok) {
        throw new Error(`Reset failed: ${movementResponse.status}/${priceResponse.status}`);
      }
      showSuccessToast('Data reset successfully!', 'bottom-right');
    } catch (error: any) {
      console.error('Error resetting data:', error);
      showErrorToast('Failed to reset data', 'bottom-right');
    }
  };

  // ------------------- ICON COMPONENTS -------------------
  const KimaruIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="7.77832" width="5" height="5" rx="1" transform="rotate(45 7.77832 0)" fill="#548DE0" />
      <rect x="12.021" y="4.24268" width="5" height="5" rx="1" transform="rotate(45 12.021 4.24268)" fill="#7134BE" />
      <rect x="7.77832" y="8.48523" width="5" height="5" rx="1" transform="rotate(45 7.77832 8.48523)" fill="#5BAFAF" />
    </svg>
  );

  const HomeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 1024 1024" fill="#000000" style={{ opacity: 1 }}>
      <path d="M946.5 505L560.1 118.8l-25.9-25.9a31.5 31.5 0 0 0-44.4 0L77.5 505a63.9 63.9 0 0 0-18.8 46c.4 35.2 29.7 63.3 64.9 63.3h42.5V940h691.8V614.3h43.4c17.1 0 33.2-6.7 45.3-18.8a63.6 63.6 0 0 0 18.7-45.3c0-17-6.7-33.1-18.8-45.2M568 868H456V664h112zm217.9-325.7V868H632V640c0-22.1-17.9-40-40-40H432c-22.1 0-40 17.9-40 40v228H238.1V542.3h-96l370-369.7l23.1 23.1L882 542.3z" stroke="#0F172A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const ExplorerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
      <path d="M13 6.66656V5.33323C12.9998 5.09941 12.938 4.86977 12.821 4.66734C12.704 4.4649 12.5358 4.2968 12.3333 4.17989L7.66667 1.51323C7.46397 1.3962 7.23405 1.33459 7 1.33459C6.76595 1.33459 6.53603 1.3962 6.33333 1.51323L1.66667 4.17989C1.46418 4.2968 1.29599 4.4649 1.17897 4.66734C1.06196 4.86977 1.00024 5.09941 1 5.33323V10.6666C1.00024 10.9004 1.06196 11.13 1.17897 11.3325C1.29599 11.5349 1.46418 11.703 1.66667 11.8199L6.33333 14.4866C6.53603 14.6036 6.76595 14.6652 7 14.6652C7.23405 14.6652 7.46397 14.6036 7.66667 14.4866L9 13.7266M4 2.84664L10 6.27997M1.19336 4.66663L7.00003 7.99996M7.00003 7.99996L12.8067 4.66663M7.00003 7.99996L7 14.6666M12.5133 11.5133L13.6667 12.6666M13 10.3333C13 11.2538 12.2538 12 11.3333 12C10.4129 12 9.66667 11.2538 9.66667 10.3333C9.66667 9.41282 10.4129 8.66663 11.3333 8.66663C12.2538 8.66663 13 9.41282 13 10.3333Z" stroke="#0F172A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const DecisionTrackerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 2V14H14M4.66667 8V11.3333H12.6667V5.33333L9.33333 8.66667L6.66667 6L4.66667 8Z" stroke="#0F172A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const DataLibraryIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M14 3.33337C14 4.43794 11.3137 5.33337 8 5.33337C4.68629 5.33337 2 4.43794 2 3.33337M14 3.33337C14 2.2288 11.3137 1.33337 8 1.33337C4.68629 1.33337 2 2.2288 2 3.33337M14 3.33337V5.33337M2 3.33337L2.00004 12.6667C1.99633 12.9873 2.22391 13.3035 2.6636 13.5887C3.10329 13.8738 3.7422 14.1196 4.52647 14.3052C5.31074 14.4908 6.21739 14.6108 7.16999 14.6552C8.12259 14.6996 9.09321 14.6669 10 14.56M14 8.00004L12 11.3334H14.6667L12.6667 14.6667M2 8.00004C2.00096 8.31173 2.22046 8.61902 2.64097 8.89741C3.06148 9.1758 3.67137 9.41758 4.42198 9.60345C5.17258 9.78932 6.04312 9.91414 6.96414 9.96795C7.88516 10.0218 8.83116 10.0031 9.72667 9.91337" stroke="#0F172A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const CausalDiagramIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0F172A" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 1 }}>
      <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3zM7 16.5l-4.74-2.85M7 16.5l5-3m-5 3v5.17m5-8.17V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5zm5 3l-5-3m5 3l4.74-2.85M17 16.5v5.17" />
      <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3l5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0zM12 8L7.26 5.15M12 8l4.74-2.85M12 13.5V8" />
    </svg>
  );

  const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
      <path d="M12.5002 13.3333C12.5002 12.2724 12.0787 11.255 11.3286 10.5048C10.5784 9.75468 9.56103 9.33325 8.50016 9.33325M8.50016 9.33325C7.4393 9.33325 6.42188 9.75468 5.67174 10.5048C4.92159 11.255 4.50016 12.2724 4.50016 13.3333M8.50016 9.33325C9.97292 9.33325 11.1668 8.13934 11.1668 6.66659C11.1668 5.19383 9.97292 3.99992 8.50016 3.99992C7.0274 3.99992 5.8335 5.19383 5.8335 6.66659C5.8335 8.13934 7.0274 9.33325 8.50016 9.33325ZM15.1668 7.99992C15.1668 11.6818 12.1821 14.6666 8.50016 14.6666C4.81826 14.6666 1.8335 11.6818 1.8335 7.99992C1.8335 4.31802 4.81826 1.33325 8.50016 1.33325C12.1821 1.33325 15.1668 4.31802 15.1668 7.99992Z" stroke="#0F172A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  // ------------------- NAV ITEMS -------------------
  const navItems: any[] = [
    //{ path: "/explorer", icon: ExplorerIcon, label: "Explorer" },
    { path: "/", icon: HomeIcon, label: "Work Hub" },
    { path: "/decision-tracker", icon: DecisionTrackerIcon, label: "Decision Tracker" },
    { path: "/data-library", icon: DataLibraryIcon, label: "Data Library" },
    { path: "https://main.d1trnioxzm5p99.amplifyapp.com/causal-decision-diagram", icon: CausalDiagramIcon, label: "CDD", isExternal: true },
  ];

  // ------------------- RENDER -------------------
  return (
    <div className="flex h-screen">
      <nav
        ref={sidebarRef}
        className={`bg-white border-r border-slate-300 flex flex-col transition-all duration-300 ease-in-out z-40 ${isExpanded ? 'w-48' : 'w-[41px]'}`}
        onMouseEnter={() => !isManual && setIsExpanded(true)}
        onMouseLeave={() => !isManual && setIsExpanded(false)}
      >
        {/* LOGO */}
        <div className="flex items-center h-12 px-3 border-b border-slate-200">
          {isExpanded ? (
            <div className="flex items-center justify-between w-full my-1">
              <Link to="/" className="flex items-center">
                <img
                  src="/assets/kimaru-logo/kimaru-092025.svg"
                  alt="Kimaru.ai logo"
                  className="h-[24px] w-[88px] flex-shrink-0"
                />
              </Link>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full h-12">
              <Link to="/" onClick={() => setIsManual(false)}>
                <div className="w-[15.556px] h-[15.556px] flex-shrink-0 my-1">
                  <KimaruIcon />
                </div>
              </Link>
            </div>
          )}
        </div>

        <div className="h-3" />

        {/* NAV ITEMS */}
        <div className="flex-1">
          <div className="space-y-1 px-0.5">
            {navItems.map((item: any) => (
              item.isExternal ? (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 h-10 p-2 rounded-md hover:bg-slate-100 transition-colors w-full ${isExpanded ? 'justify-start' : 'justify-center'}`}
                >
                  <div className="w-4 h-4 flex-shrink-0">
                    <item.icon />
                  </div>
                  {isExpanded && (
                    <Typography className="text-slate-900 font-medium text-sm whitespace-nowrap">
                      {item.label}
                    </Typography>
                  )}
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 h-10 p-2 rounded-md hover:bg-slate-100 transition-colors w-full ${isExpanded ? 'justify-start' : 'justify-center'}`}
                >
                  <div className="w-4 h-4 flex-shrink-0">
                    <item.icon />
                  </div>
                  {isExpanded && (
                    <Typography className="text-slate-900 font-medium text-sm whitespace-nowrap">
                      {item.label}
                    </Typography>
                  )}
                </Link>
              )
            ))}
          </div>
        </div>

        {/* USER DROPDOWN */}
        <div className="border-t border-slate-200">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`flex items-center gap-3 h-10 px-3 hover:bg-slate-100 transition-colors w-full ${isExpanded ? 'justify-start' : 'justify-center'}`}
            >
              <div className="w-4 h-4 flex-shrink-0">
                <UserIcon />
              </div>
              {isExpanded && (
                <div className="flex items-center gap-1">
                  <Typography className="text-slate-900 font-medium text-sm whitespace-nowrap">
                    {userName}
                  </Typography>
                  <img
                    src="/assets/icons/ChevronDown.svg"
                    className={`w-3 h-3 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    alt="Dropdown Icon"
                  />
                </div>
              )}
            </button>

            {isDropdownOpen && (
              <div className={`absolute ${isExpanded ? 'left-0' : 'left-12'} bottom-full mb-2 w-48 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-50`}>
                <button
                  onClick={handleResetData}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors duration-150 flex items-center gap-2"
                >
                  <DataObjectIcon style={{ fontSize: 16 }} />
                  <span>Data</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors duration-150 flex items-center gap-2"
                >
                  <img src="/assets/icons/Logout.svg" alt="" className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="flex-1 flex flex-col">
        <div className="flex-1 overflow-auto bg-gray-50">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Navbar;
